import ArrowDown from '@atoms/ArrowDown';
import BPSubProdSelectedNominal from '@atoms/BPSubProdSelectedNominal';
import { Button, Divider, Text, View } from '@components/primitives';
import ModalHeader from '@components/primitives/ModalHeader';
import useBPMutation from '@customHooks/useBPRequest';
import navigationConstants from '@navigation/navigationConstants';
import { useNavigation } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { logNominalsPress } from '@screens/GGBillPayments/GGNominals/analyticsNominals';
import { getStore } from '@store/storeUtils';
import colors from '@theme/colors';
import { scale, verticalScale } from '@utils/normalize';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, Modal, TouchableOpacity } from 'react-native';
import logSubProdNominalFailure from './analyticsSubProducts';
import setOfInlineFunctions from './bpSubProdNominalUtils';
import styles from './styles';

interface Nominal {
  code: string;
  availability: boolean;
  iconLink?: string;
  name: string;
  nominal: {
    amount: number;
    value: number;
  };
}

interface ProductConfig {
  hasAccountInput: boolean;
  transaction_category_id?: string;
  reason?: string;
  reasonId?: string;
  [key: string]: any;
}

interface ProductApiData {
  prefix: string;
  [key: string]: any;
}

interface BPPhone {
  mobileNumber: string;
}

interface BPStore {
  bpPhone: BPPhone;
  selectedBPAccount: {
    id: string;
    amplitudeAccountName: string;
  };
  validatedReferralCode: string | null;
  meAmplitudeContext: any[];
}

interface BPSubProdNominalsProps {
  source: string;
  productNominals: Record<string, Nominal[]>;
  selectedProductItem: string;
  productId: number;
  productConfig: ProductConfig;
  productApiData: ProductApiData;
  isFavourite: boolean;
}

function getItemSeperatorComponent() {
  return () => <Divider color={colors.disabled.smoke} height="1px" />;
}

const BPSubProdNominals: React.FC<BPSubProdNominalsProps> = ({
  source = '',
  productNominals = {},
  selectedProductItem = '',
  productId = 1,
  productConfig = { hasAccountInput: false },
  productApiData = { prefix: '' },
  isFavourite = false,
}) => {
  const [openRTS, setOpenRTS] = useState(false);
  const { bpPhone, selectedBPAccount, validatedReferralCode, meAmplitudeContext } = getStore() as BPStore;
  const { mobileNumber } = bpPhone;
  const { t } = useTranslation('common');
  const [selectedNominal, setSelectedNominal] = useState<Nominal>(productNominals[selectedProductItem][0]);
  const [showNominalsModal, setShowNominalsModal] = useState(false);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string | null>(
    productConfig.hasAccountInput ? null : mobileNumber,
  );
  const navigation = useNavigation();
  const [bpReqData, setBpReqData] = useState<Record<string, any>>({});
  const { requestBP, isBPReqError, bpError, apiPayload, isBPReqLoading } = useBPMutation({ setOpenRTS, setBpReqData });
  // Remove ref - control with index prop

  useEffect(() => {
    setSelectedAccountNumber(bpPhone.mobileNumber);
  }, [bpPhone]);

  useEffect(() => {
    setSelectedNominal(productNominals[selectedProductItem][0]);
  }, [selectedProductItem]);

  useEffect(() => {
    logNominalsPress(selectedAccountNumber, selectedNominal, productConfig, source, validatedReferralCode, isFavourite);
  }, [selectedNominal]);

  const { btnToShowNominals, onPressSubProdOrNominals, closeModalCallBack, listOfNominalsForSubProducts } =
    setOfInlineFunctions({
      setSelectedNominal,
      setShowNominalsModal,
      productId,
      navigation,
      selectedProductItem,
      productNominals,
      selectedNominal,
      selectedAccountNumber,
      selectedBPAccount,
      productConfig,
      productApiData,
      requestBP,
      validatedReferralCode,
      source,
      meAmplitudeContext,
      isFavourite,
    });

  useEffect(() => {
    if (isBPReqError && bpError) {
      logSubProdNominalFailure(selectedAccountNumber, productConfig, apiPayload, t, bpError, source);
    }
  }, [isBPReqError, bpError]);

  useEffect(() => {
    if (openRTS && bpReqData && Object.keys(bpReqData).length > 0) {
      // openRTS={openRTS}
      // setOpenRTS={setOpenRTS}
      // productConfig={productConfig}
      // productApiData={productApiData}
      // selectedAccountNumber={selectedAccountNumber}
      // nominalSelected={selectedNominal}
      // selectedProductItem={selectedProductItem}
      // bpReqData={bpReqData}
      navigation.navigate(navigationConstants.GG_PAYMENT_SUMMARY, {
        bpReqData,
        productConfig,
        selectedBPAccountForConfirm: selectedBPAccount,
        source,
        productApiData,
        selectedAccountNumber,
        nominalSelected: selectedNominal,
        selectedProductItem,
      });
      setOpenRTS(false);
    }
  }, [openRTS, bpReqData]);

  // Remove useEffect - control with index prop
  return (
    <View style={{ marginTop: verticalScale(32), marginHorizontal: scale(16) }}>
      <Text testID="txtChooseProduct" variant="lg-bold" color={colors.secondary.orchid}>
        {t('bp.subProductNominals')}
      </Text>
      <View>
        <TouchableOpacity testID="btnNominalDropDown" style={styles.selectedNominalStyles} onPress={btnToShowNominals}>
          <BPSubProdSelectedNominal
            selectedAccountNumber={selectedAccountNumber}
            selectedNominal={selectedNominal}
            productConfig={productConfig}
          />
          <ArrowDown />
        </TouchableOpacity>
        <Divider
          height="1px"
          color={colors.neutral.darkMist}
          style={{
            marginTop: verticalScale(8),
          }}
        />
        <Button
          testID="btnRequestFromSubProducts"
          style={[appStyles.marginTop(24), { paddingVertical: verticalScale(8) }]}
          isDisabled={isBPReqLoading}
          isLoading={isBPReqLoading}
          variant="solid"
          onPress={onPressSubProdOrNominals}>
          <Text variant="md-bold" color={colors.neutral.cotton}>
            {t('common.continue')}
          </Text>
        </Button>
      </View>
      <Modal
        visible={showNominalsModal}
        animationType="none"
        presentationStyle="overFullScreen"
        transparent
        onRequestClose={closeModalCallBack}>
        <TouchableOpacity
          style={[styles.backdrop, { backgroundColor: colors.overlay.backdrop }]}
          activeOpacity={1}
          onPress={closeModalCallBack}>
          <TouchableOpacity
            style={[styles.modalContent, { backgroundColor: colors.neutral.cotton }]}
            activeOpacity={1}
            onPress={e => e.stopPropagation()}>
            <ModalHeader title={t('bp.subProductNominals')} onClose={closeModalCallBack} />
            <View style={styles.modalListContainer}>
              <FlatList
                ItemSeparatorComponent={getItemSeperatorComponent()}
                data={productNominals[selectedProductItem]}
                renderItem={listOfNominalsForSubProducts}
              />
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

export default BPSubProdNominals;
